import { useCallback, useEffect, useMemo, useState } from "react";
import type { Field, Row, TabDef } from "../types";
import { api, ApiError, type OpcoesDocumento } from "../api";
import { formatCell, formatDate } from "../format";
import { ordenarPorNome } from "../ordem";
import RecordForm from "./RecordForm";
import PlanoAcaoModal from "./PlanoAcaoModal";
import DocumentoAprovacao, { ANEXO_VAZIO, type AnexoPendente } from "./DocumentoAprovacao";
import DocumentosConfigModal from "./DocumentosConfigModal";
import ErrorState from "./ErrorState";
import { usePodeExcluir } from "../permissoes";
import { IconSearch, IconPlus, IconEdit, IconTrash, IconSparkles, IconDownload, IconSettings } from "../icons";

interface Props {
  tab: TabDef;
  ncs: Row[];
  onDataChanged: () => void; // avisa o App para recarregar NCs/dashboard
}

export default function CrudView({ tab, ncs, onDataChanged }: Props) {
  // Excluir registro é só de administrador (regra do Portal) — o servidor
  // recusa de qualquer jeito; aqui a lixeira nem aparece para os demais.
  const podeExcluir = usePodeExcluir();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [planoFor, setPlanoFor] = useState<Row | null>(null);
  // Aba Documentos: download da versão vigente por linha + arquivo e aprovação no pop-up.
  const [busyArquivo, setBusyArquivo] = useState<number | null>(null);
  const [anexo, setAnexo] = useState<AnexoPendente>(ANEXO_VAZIO);
  const [podeEnviar, setPodeEnviar] = useState(false);
  const [recargaFluxo, setRecargaFluxo] = useState(0);
  // Documentos que esperam a aprovação/publicação de quem está logado.
  const [aguardandoVoce, setAguardandoVoce] = useState<number[]>([]);
  const [soAguardando, setSoAguardando] = useState(false);
  // Configuração dos documentos: tipos (com o próximo código) e setores do RH
  // para o formulário; o botão "Configurar" é só do administrador.
  const [opcoes, setOpcoes] = useState<OpcoesDocumento | null>(null);
  const [adminDocs, setAdminDocs] = useState(false);
  const [configAberta, setConfigAberta] = useState(false);
  // Filtro "N para revisar": revisão periódica vencida ou chegando.
  const [soRevisar, setSoRevisar] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.list(tab.resource));
      if (tab.aprovacao) {
        // Falhar aqui não pode esconder a lista: sem a resposta, só não marca nada.
        api.documentos
          .papel()
          .then((p) => {
            setAguardandoVoce(p.aguardandoVoce);
            setAdminDocs(p.admin);
          })
          .catch(() => setAguardandoVoce([]));
        // Sem as opções, o formulário usa a lista fixa da aba (config.ts).
        api.documentos
          .opcoes()
          .then(setOpcoes)
          .catch(() => {});
      }
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, [tab.resource, tab.aprovacao]);

  useEffect(() => {
    setSearch("");
    load();
  }, [load]);

  // Acabou o que esperava pela pessoa: o filtro volta a mostrar tudo.
  useEffect(() => {
    if (soAguardando && aguardandoVoce.length === 0) setSoAguardando(false);
  }, [soAguardando, aguardandoVoce]);

  const paraRevisar = useMemo(
    () => (tab.aprovacao ? rows.filter((r) => precisaRevisar(r)).length : 0),
    [rows, tab.aprovacao],
  );
  useEffect(() => {
    if (soRevisar && paraRevisar === 0) setSoRevisar(false);
  }, [soRevisar, paraRevisar]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let achados = !q
      ? rows
      : rows.filter((r) =>
          tab.fields.some((f) => {
            const value = f.ref === "nao_conformidades" ? r.ref_label : r[f.name];
            return String(value ?? "").toLowerCase().includes(q);
          }),
        );
    if (soAguardando) achados = achados.filter((r) => aguardandoVoce.includes(r.id));
    if (soRevisar) achados = achados.filter((r) => precisaRevisar(r));
    // Aba de cadastro por nome (ex.: cliente avaliado) abre em ordem alfabética;
    // as demais (NCs, auditorias, ações por prazo) seguem a ordem da API.
    const campo = tab.ordenarPor;
    return campo ? ordenarPorNome(achados, (r) => r[campo]) : achados;
  }, [rows, search, tab.fields, tab.ordenarPor, soAguardando, aguardandoVoce, soRevisar]);

  function abrir(row: Row | null) {
    setEditing(row);
    setAnexo(ANEXO_VAZIO);
    setPodeEnviar(false);
    setFormOpen(true);
  }

  async function handleSubmit(data: Row, acao?: string) {
    let salvo: Row;
    if (editing) {
      salvo = await api.update(tab.resource, editing.id, data);
    } else {
      salvo = await api.create(tab.resource, data);
      // Daqui em diante o pop-up edita este registro: se o arquivo falhar e a
      // pessoa clicar em Salvar de novo, o documento não é criado em dobro.
      setEditing(salvo);
    }

    if (tab.aprovacao) {
      let passo: "anexar" | "enviar" = "anexar";
      try {
        if (anexo.file || anexo.alterado) {
          await api.documentos.anexar(salvo.id, anexo);
          setAnexo(ANEXO_VAZIO);
          setRecargaFluxo((n) => n + 1);
        }
        passo = "enviar";
        if (acao === "enviar") await api.documentos.acao(salvo.id, "enviar");
      } catch (err) {
        void load();
        onDataChanged();
        const motivo = err instanceof Error ? err.message : String(err);
        throw new Error(
          passo === "anexar"
            ? `O documento foi salvo, mas o arquivo não foi anexado: ${motivo}`
            : `O documento foi salvo, mas não foi enviado para aprovação: ${motivo}`,
        );
      }
    }

    setFormOpen(false);
    await load();
    onDataChanged();
  }

  async function handleDelete(row: Row) {
    if (!confirm("Tem certeza que deseja excluir este registro?")) return;
    try {
      await api.remove(tab.resource, row.id);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  async function baixarArquivo(row: Row) {
    setBusyArquivo(row.id);
    try {
      await api.documentos.download(row.id, row.arquivo_nome);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao baixar o documento");
    } finally {
      setBusyArquivo(null);
    }
  }

  const recarregarDepoisDeDecidir = useCallback(() => {
    void load();
    onDataChanged();
  }, [load, onDataChanged]);

  /**
   * Documentos: tipo e setor vêm da configuração (tipos cadastrados e setores
   * do RH) e o código mostra o que vai sair para o tipo escolhido. "Quem
   * aprova" lista as pessoas ativas do RH, com os sugeridos do setor escolhido
   * primeiro (os marcados em "Aprova" no Configurar; sem ninguém, o gestor do
   * setor e os diretores).
   */
  const ajustarCampo = useCallback(
    (f: Field, form: Row): Field => {
      if (!opcoes) return f;
      if (f.name === "aprovador") {
        const setor = String(form.departamento ?? "");
        const sugeridos = (opcoes.aprovadoresPorSetor[setor] ?? opcoes.aprovadoresPorSetor[""] ?? []).map(chaveNome);
        const opcao = (p: { nome: string; cargo: string | null }) => ({
          valor: p.nome,
          rotulo: p.cargo ? `${p.nome} — ${p.cargo}` : p.nome,
        });
        const doSetor = opcoes.pessoas.filter((p) => sugeridos.includes(chaveNome(p.nome)));
        const outras = opcoes.pessoas.filter((p) => !sugeridos.includes(chaveNome(p.nome)));
        return {
          ...f,
          grupos: [
            ...(doSetor.length > 0
              ? [{ rotulo: setor ? `Sugeridos para ${setor}` : "Sugeridos", opcoes: doSetor.map(opcao) }]
              : []),
            { rotulo: doSetor.length > 0 ? "Outras pessoas" : "Pessoas", opcoes: outras.map(opcao) },
          ],
        };
      }
      if (f.name === "tipo") return { ...f, options: opcoes.tipos.map((t) => t.nome) };
      if (f.name === "departamento" && opcoes.setores.length > 0) return { ...f, options: opcoes.setores };
      if (f.name === "codigo") {
        const tipo = opcoes.tipos.find((t) => t.nome === form.tipo);
        return {
          ...f,
          placeholder: tipo ? `${tipo.proximo} — automático ao salvar` : "Escolha o tipo: o código sai automático",
        };
      }
      return f;
    },
    [opcoes],
  );

  if (error)
    return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          {tab.aprovacao && paraRevisar > 0 && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              title={`Revisão periódica vencida ou vencendo nos próximos ${opcoes?.avisoDias ?? 60} dias`}
              onClick={() => setSoRevisar((v) => !v)}
            >
              {soRevisar ? "Mostrar todos" : `${paraRevisar} para revisar`}
            </button>
          )}
          {tab.aprovacao && aguardandoVoce.length > 0 && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              title="Documentos esperando a sua aprovação ou publicação"
              onClick={() => setSoAguardando((v) => !v)}
            >
              {soAguardando ? "Mostrar todos" : `${aguardandoVoce.length} aguardando você`}
            </button>
          )}
          <span className="badge">{filtered.length} registro(s)</span>
          {tab.aprovacao && adminDocs && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              title="Tipos de documento, códigos, revisão periódica, sugeridos para aprovar e quem publica cada setor"
              onClick={() => setConfigAberta(true)}
            >
              <IconSettings size={15} /> Configurar
            </button>
          )}
          <button className="btn-primary" onClick={() => abrir(null)}>
            <IconPlus size={17} /> Novo
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty">Nenhum registro encontrado.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                {tab.fields.filter((f) => !f.hideInTable).map((f) => (
                  <th key={f.name}>{f.label}</th>
                ))}
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const esperaVoce = tab.aprovacao && aguardandoVoce.includes(row.id);
                return (
                  <tr key={row.id}>
                    {tab.fields.filter((f) => !f.hideInTable).map((f) => (
                      <td key={f.name}>
                        {f.name === "proxima_revisao" ? <ProximaRevisao row={row} /> : formatCell(f, row)}
                        {esperaVoce && f.name === "etapa_aprovacao" && <strong> · você</strong>}
                      </td>
                    ))}
                    <td className="col-actions">
                      {tab.aiPlano && (
                        <button
                          className="icon-btn icon-btn-ia"
                          title="Gerar plano de ação (IA)"
                          onClick={() => setPlanoFor(row)}
                        >
                          <IconSparkles size={17} />
                        </button>
                      )}
                      {tab.aprovacao && row.arquivo_nome && (
                        <button
                          className="icon-btn icon-btn-ok"
                          title={`Baixar a versão vigente: ${row.arquivo_nome}`}
                          disabled={busyArquivo !== null}
                          onClick={() => baixarArquivo(row)}
                        >
                          <IconDownload size={17} />
                        </button>
                      )}
                      <button
                        className="icon-btn"
                        title={
                          esperaVoce
                            ? row.etapa_aprovacao === "Aguardando publicação"
                              ? "Abrir para publicar"
                              : "Abrir para aprovar"
                            : "Editar"
                        }
                        disabled={busyArquivo === row.id}
                        onClick={() => abrir(row)}
                      >
                        <IconEdit size={17} />
                      </button>
                      {podeExcluir && (
                        <button
                          className="icon-btn icon-btn-danger"
                          title="Excluir"
                          disabled={busyArquivo === row.id}
                          onClick={() => handleDelete(row)}
                        >
                          <IconTrash size={17} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {formOpen && (
        <RecordForm
          title={editing ? `Editar — ${tab.label}` : `Novo — ${tab.label}`}
          fields={tab.fields}
          initial={editing}
          ncs={ncs}
          largo={tab.aprovacao}
          onCancel={() => setFormOpen(false)}
          onSubmit={handleSubmit}
          rotuloFechar={tab.aprovacao && editing ? "Fechar" : "Cancelar"}
          ajustarCampo={tab.aprovacao ? ajustarCampo : undefined}
          acaoExtra={
            tab.aprovacao && podeEnviar
              ? { acao: "enviar", rotulo: "Salvar e enviar para aprovação", rotuloSalvando: "Enviando..." }
              : null
          }
        >
          {tab.aprovacao && (
            <DocumentoAprovacao
              documentoId={editing?.id ?? null}
              anexo={anexo}
              onAnexo={setAnexo}
              onPodeEnviar={setPodeEnviar}
              onMudou={recarregarDepoisDeDecidir}
              recarga={recargaFluxo}
            />
          )}
        </RecordForm>
      )}

      {configAberta && (
        <DocumentosConfigModal
          onClose={() => setConfigAberta(false)}
          onSalvo={() => {
            setConfigAberta(false);
            void load();
          }}
        />
      )}

      {planoFor && (
        <PlanoAcaoModal
          nc={planoFor}
          onClose={() => setPlanoFor(null)}
          onApplied={() => {
            load();
            onDataChanged();
          }}
        />
      )}
    </div>
  );
}

/** Nome comparável (sem acento, maiúscula ou espaço sobrando) — os sugeridos vêm do RH com a grafia de lá. */
const chaveNome = (v: unknown) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** Revisão periódica vencida ou chegando (a lista do servidor já traz a situação). */
const precisaRevisar = (r: Row) => r.validade_situacao === "vencida" || r.validade_situacao === "vencendo";

/** Célula "Próxima revisão": a data e, se estiver perto ou já passou, o aviso. */
function ProximaRevisao({ row }: { row: Row }) {
  if (!row.proxima_revisao) return <>—</>;
  const dias = Number(row.validade_dias);
  return (
    <span className="doc-validade">
      {formatDate(row.proxima_revisao)}
      {row.validade_situacao === "vencida" && <span className="pill pill-danger">Vencida</span>}
      {row.validade_situacao === "vencendo" && (
        <span className="pill pill-warn">{dias === 0 ? "Vence hoje" : `Em ${dias} dia${dias === 1 ? "" : "s"}`}</span>
      )}
    </span>
  );
}
